/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, type ReactElement } from 'react';
import { Await } from 'react-router';
import AnnouncementBanner from './index';
import AnnouncementCarousel from './announcement-carousel';
import type { PromoSlide } from './get-promo-slides.server';

interface AnnouncementBannerPromoProps {
    /**
     * Result of `getPromoSlides()`, streamed from the route loader (same shape other deferred
     * loader data takes). `undefined` when no promo content is configured — renders nothing.
     */
    slides: PromoSlide[] | Promise<PromoSlide[]> | undefined;
}

/**
 * Forces the utility-bar house style (Figma node `3498:20545`): plain text on the utility bar's
 * own light-grey/dark-text surface, left-aligned, no independent colour block. `AnnouncementBanner`
 * is a shared, reusable component — elsewhere (the separate `announcement` Page Designer region)
 * its colour-scheme/alignment attributes are genuinely author-configurable — but the promo slot is
 * header chrome, not decorative content (see `utility-bar.tsx`'s "chrome, not content" doc comment),
 * so its look is fixed here rather than left to whatever a slide's own data happens to say.
 * `bg-transparent`/`text-header-banner-foreground` override `AnnouncementBanner`'s default
 * colour-scheme classes via `cn()`'s tailwind-merge conflict resolution (last utility in a
 * conflicting group wins), and `px-0 md:px-0` cancels its default horizontal padding, since
 * `section-container` already supplies the utility bar's edge padding.
 */
const PROMO_SLOT_CLASS = 'bg-transparent px-0 md:px-0 text-header-banner-foreground';

function renderSlides(slides: PromoSlide[]): ReactElement | null {
    if (slides.length === 0) return null;

    return (
        <AnnouncementCarousel>
            {slides.map((slide, index) => (
                <AnnouncementBanner
                    // eslint-disable-next-line react/no-array-index-key -- placeholder slides have no stable id
                    key={index}
                    message={slide.message}
                    linkUrl={slide.linkUrl}
                    linkText={slide.linkText}
                    alignment="left"
                    className={PROMO_SLOT_CLASS}
                />
            ))}
        </AnnouncementCarousel>
    );
}

/**
 * Renders the header utility bar's rotating promo slot from `getPromoSlides()` — currently a
 * local placeholder, eventually a Sanity-backed fetch (see `get-promo-slides.server.ts`).
 * Deliberately not Page Designer-driven: see the `announcement-banner` spec's "Content source
 * is pluggable, currently a local placeholder" requirement. Not to be confused with the
 * separate, unrelated Page Designer `announcement` region on `HeaderMetadata` — that region
 * stays independent per the `site-header` spec and is untouched here.
 */
export function AnnouncementBannerPromo({ slides }: AnnouncementBannerPromoProps): ReactElement | null {
    if (slides === undefined) return null;

    if (slides instanceof Promise) {
        return (
            <Suspense fallback={null}>
                <Await resolve={slides} errorElement={null}>
                    {renderSlides}
                </Await>
            </Suspense>
        );
    }

    return renderSlides(slides);
}
